import React from "react";
import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AccountPopover from "../AccountPopover/AccountPopover";
import NavBar from "../NavBar/NavBar";
import { CompanyAvatar } from "../CompanyAvatar/CompanyAvatar";
import { WapNavIcon } from "../Icons/WapNavIcon/WapNavIcon";
import { LocationIcon } from "../Icons/LocationIcon/LocationIcon";
import { InstagramIconAvatar } from "../Icons/InstagramIcon/InstagramIcon";
import { FacebookIconAvatar } from "../Icons/FacebookIcon/FacebookIcon";
import { GOOGLE_MAPS_URL, WHATSAPP_URL, FACEBOOK_URL, INSTAGRAM_URL } from "../../utils/contact";
import { gaEvent } from "../../analytics";
import CartButton from "../CartButton/CartButton";
import { useCartDrawer } from "../CartComponent/CartDrawerProvider";
import HeaderSearch, { HEADER_BAR_ATTR } from "./HeaderSearch";

interface HeaderBarProps {
  /**
   * Whether the storefront search belongs in this header.
   *
   * `PageLayout` renders one header for every route, admin included, and a catalog search over an
   * AG Grid of items would filter nothing.
   */
  showSearch?: boolean;
}

const HeaderBarContainer = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar,
  display: "flex",
  flexDirection: "column",
  borderRadius: 0,
  boxShadow: theme.shadows[4],
  backgroundColor: theme.palette.common.white,
}));

const IconsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: 60,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    height: 103,
    // Tighter than the 32px padding and 4px-per-side icon margins this used to carry. The content
    // column is only 50% of the viewport at `lg`, so the header's own chrome is what the search
    // field is competing with — at the old spacing the placeholder truncated mid-word.
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
}));

const IconGroup = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});

const IconLink = styled(Box)({
  cursor: "pointer",
  marginLeft: 2,
  marginRight: 2,
});

/** Inline between the icon groups on desktop; takes whatever width the icons leave. */
const DesktopSearchSlot = styled(Box)({
  flex: 1,
  maxWidth: 560,
});

/** Full-bleed second row on mobile, where an inline field would be unusably narrow. */
const MobileSearchRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 1, 1),
}));

interface SocialLinkProps {
  href: string;
  onClick: () => void;
  children: React.ReactNode;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, onClick, children }) => (
  <IconLink>
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
      {children}
    </a>
  </IconLink>
);

const HeaderBar: React.FC<HeaderBarProps> = ({ showSearch = true }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  // The drawer itself lives in `CartDrawerProvider` since EB-556 — the product page has to open it
  // too, and it renders below this header rather than inside it.
  const { openCart } = useCartDrawer();

  // Rendered once and placed by `isDesktop` rather than mounted twice and hidden with CSS: two
  // copies would put two search inputs and two of every social link in the DOM, which screen
  // readers announce twice and `getByRole` queries find twice.
  const facebook = (
    <SocialLink
      href={FACEBOOK_URL}
      onClick={() => gaEvent("social_click", { platform: "facebook", source: "header" })}
    >
      <FacebookIconAvatar />
    </SocialLink>
  );

  const instagram = (
    <SocialLink
      href={INSTAGRAM_URL}
      onClick={() => gaEvent("social_click", { platform: "instagram", source: "header" })}
    >
      <InstagramIconAvatar />
    </SocialLink>
  );

  const whatsapp = (
    <SocialLink href={WHATSAPP_URL} onClick={() => gaEvent("whatsapp_click", { source: "header" })}>
      <WapNavIcon />
    </SocialLink>
  );

  const maps = (
    <SocialLink href={GOOGLE_MAPS_URL} onClick={() => gaEvent("maps_click", { source: "header" })}>
      <LocationIcon />
    </SocialLink>
  );

  return (
    <>
      {/* Marked so `HeaderSearch` can measure this bar rather than assume its height — it differs
          between the one-row desktop layout and the two-row mobile one. */}
      <HeaderBarContainer {...{ [HEADER_BAR_ATTR]: "" }}>
        <IconsRow>
          <IconGroup>
            <RouterLink to="/">
              <CompanyAvatar />
            </RouterLink>
            <Box ml={1}>
              <NavBar />
            </Box>
            {facebook}
            {instagram}
          </IconGroup>

          {/* On mobile the map pin and WhatsApp stay in row 1 beside the socials; on desktop they
              move to the right of the search field, which is what splits the row in two. */}
          {!isDesktop && (
            <IconGroup>
              {maps}
              {whatsapp}
            </IconGroup>
          )}

          {showSearch && isDesktop && (
            <DesktopSearchSlot>
              <HeaderSearch />
            </DesktopSearchSlot>
          )}

          <IconGroup>
            {isDesktop && whatsapp}
            {isDesktop && maps}
            <CartButton
              onClick={() => openCart("header")}
              minWidth="auto"
              tooltipPlacement="bottom"
              sx={{ boxShadow: 0, border: "none", bgcolor: "transparent", minWidth: "auto" }}
            />
            <AccountPopover />
          </IconGroup>
        </IconsRow>

        {showSearch && !isDesktop && (
          <MobileSearchRow>
            <HeaderSearch />
          </MobileSearchRow>
        )}
      </HeaderBarContainer>
    </>
  );
};

export default HeaderBar;
